import type { Recipe } from '@/features/recipes/types';
import type { Menu } from '@/features/menus/types';
import type {
  MenuSelection,
  RecipeSelection,
  AggregatedIngredient,
  IngredientSource,
} from '../../types';

function addIngredientToMap(
  map: Map<string, AggregatedIngredient>,
  itemId: string,
  itemName: string,
  unit: string,
  quantity: number,
  source: IngredientSource
): void {
  const existing = map.get(itemId);
  if (existing) {
    existing.totalQuantity += quantity;
    existing.sources.push(source);
  } else {
    map.set(itemId, {
      itemId,
      itemName,
      sku: itemId.slice(0, 8).toUpperCase(),
      unit,
      totalQuantity: quantity,
      sources: [source],
    });
  }
}

function processMenuIngredients(
  map: Map<string, AggregatedIngredient>,
  menuSel: MenuSelection,
  menu: Menu,
  recipes: Recipe[]
): void {
  for (const menuItem of menu.items) {
    const recipe = recipes.find((r) => r.recipeId === menuItem.recipeId);
    if (!recipe) continue;

    for (const ing of recipe.ingredients) {
      const quantity = ing.quantity * menuSel.paxCount;
      const source: IngredientSource = {
        type: 'menu',
        id: menu.menuId,
        name: `${menu.name} - ${recipe.name}`,
        quantity,
      };
      addIngredientToMap(map, ing.itemId, ing.itemName, ing.unit, quantity, source);
    }
  }
}

function processRecipeIngredients(
  map: Map<string, AggregatedIngredient>,
  recipeSel: RecipeSelection,
  recipe: Recipe
): void {
  for (const ing of recipe.ingredients) {
    const quantity = ing.quantity * recipeSel.portions;
    const source: IngredientSource = {
      type: 'recipe',
      id: recipe.recipeId,
      name: recipe.name,
      quantity,
    };
    addIngredientToMap(map, ing.itemId, ing.itemName, ing.unit, quantity, source);
  }
}

export function aggregateIngredients(
  selectedMenus: MenuSelection[],
  selectedRecipes: RecipeSelection[],
  menus: Menu[],
  recipes: Recipe[]
): AggregatedIngredient[] {
  const ingredientMap = new Map<string, AggregatedIngredient>();

  for (const menuSel of selectedMenus) {
    const menu = menus.find((m) => m.menuId === menuSel.menuId);
    if (menu) {
      processMenuIngredients(ingredientMap, menuSel, menu, recipes);
    }
  }

  for (const recipeSel of selectedRecipes) {
    const recipe = recipes.find((r) => r.recipeId === recipeSel.recipeId);
    if (recipe) {
      processRecipeIngredients(ingredientMap, recipeSel, recipe);
    }
  }

  return Array.from(ingredientMap.values());
}
